// HATOM NFTs surface — top-level tab in App.tsx switches between this and
// the existing Staked Tokens dashboard. Three sub-tabs:
//   Swap     — NFT-holder side: pick a pool, swap your HLSR for instant WEGLD
//   My Pool  — pool-creator side: create/fund/withdraw/close own pool
//   Admin    — owner only: pause flags, min deposit, no-swap fee, profit wallet

import { Fragment, useEffect, useMemo, useState } from "react";
import type { AppConfig } from "../config";
import * as E from "../escrow_nft";
import { fmtAmount, parseAmount } from "../escrow";
import { AddressChip, ExplorerButton, CopyButton } from "../ui";

type SubTab = "swap" | "mypool" | "admin";

export default function HatomNfts({
  config,
  address,
  isOwner,
}: {
  config: AppConfig;
  address: string;
  isOwner: boolean;
}) {
  const [sub, setSub] = useState<SubTab>("swap");
  const [nftCfg, setNftCfg] = useState<E.NftConfig | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pools, setPools] = useState<E.NftPool[]>([]);
  const [myNfts, setMyNfts] = useState<E.UserHlsrNft[]>([]);
  const [myPool, setMyPool] = useState<E.NftPool | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Tell escrow_nft.ts who the connected wallet is so adminFetch can
  // request a signature when needed.
  useEffect(() => { E.setAdminAddress(address); }, [address]);

  // Boot: load on-chain config, all pools, user's HLSR NFTs, user's pool.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr(null);
      try {
        const c = await E.getNftConfig(config);
        if (cancelled) return;
        setNftCfg(c);

        const [allPools, userNfts, mp] = await Promise.all([
          E.getAllNftPools(config),
          E.getUserHlsrNfts(config, address, c.acceptedNftCollection),
          E.getNftPool(config, address),
        ]);
        if (cancelled) return;
        setPools(allPools);
        setMyNfts(userNfts);
        setMyPool(mp.exists ? mp : null);
      } catch (e) {
        if (!cancelled) setErr(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config, address, refreshCounter]);

  function onDone() {
    setRefreshCounter((n) => n + 1);
  }

  return (
    <div className="main">
      {/* sub-tab nav */}
      <div className="row" style={{ gap: "0.4rem", marginBottom: "1.2rem" }}>
        <SubTabBtn active={sub === "swap"} onClick={() => setSub("swap")}>Swap</SubTabBtn>
        <SubTabBtn active={sub === "mypool"} onClick={() => setSub("mypool")}>My Pool</SubTabBtn>
        {isOwner && (
          <SubTabBtn active={sub === "admin"} onClick={() => setSub("admin")}>Admin</SubTabBtn>
        )}
      </div>

      {err && (
        <div className="card" style={{ marginBottom: "1rem", borderColor: "var(--danger)" }}>
          <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>
        </div>
      )}

      {!nftCfg ? (
        <p className="muted">Loading on-chain config…</p>
      ) : nftCfg.paused && sub !== "mypool" && sub !== "admin" ? (
        <div className="card">
          <h3 style={{ color: "var(--danger)" }}>⏸ Contract paused</h3>
          <p className="muted">
            Swaps and new pool creation are paused by the owner. Existing pool
            creators can still withdraw from their pools (My Pool tab).
          </p>
        </div>
      ) : sub === "swap" ? (
        <SwapView
          config={config}
          address={address}
          nftCfg={nftCfg}
          pools={pools}
          myNfts={myNfts}
          onDone={onDone}
        />
      ) : sub === "mypool" ? (
        <MyPoolView
          config={config}
          address={address}
          nftCfg={nftCfg}
          myPool={myPool}
          onDone={onDone}
        />
      ) : (
        <AdminView config={config} address={address} nftCfg={nftCfg} onDone={onDone} />
      )}
    </div>
  );
}

function SubTabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="ghost"
      style={{
        padding: "0.4rem 0.9rem",
        background: active ? "var(--bg-elev-2)" : "transparent",
        borderColor: active ? "var(--accent)" : "var(--border)",
        color: active ? "var(--text)" : "var(--text-dim)",
      }}
    >
      {children}
    </button>
  );
}

// ─── Swap view ──────────────────────────────────────────────────────
function SwapView({
  config,
  address,
  nftCfg,
  pools,
  myNfts,
  onDone,
}: {
  config: AppConfig;
  address: string;
  nftCfg: E.NftConfig;
  pools: E.NftPool[];
  myNfts: E.UserHlsrNft[];
  onDone: () => void;
}) {
  const [selectedNonce, setSelectedNonce] = useState<number | null>(
    myNfts[0]?.nonce ?? null,
  );
  useEffect(() => {
    if (myNfts.length > 0 && selectedNonce == null) {
      setSelectedNonce(myNfts[0].nonce);
    }
  }, [myNfts, selectedNonce]);

  const nft = useMemo(
    () => myNfts.find((n) => n.nonce === selectedNonce) ?? null,
    [myNfts, selectedNonce],
  );

  // Per-pool computation; sorting differs by section (see below).
  const ranked = useMemo(() => {
    if (!nft) return [];
    return pools.map((p) => {
      const platformFee = (nft.faceValue * BigInt(p.spreadBps)) / 20000n;
      const normalPayout = (nft.faceValue * BigInt(10000 - p.spreadBps)) / 10000n;
      const poolAfterFee = p.wegld > platformFee ? p.wegld - platformFee : 0n;
      const maxPayout = poolAfterFee > normalPayout ? normalPayout : poolAfterFee;
      const fullyCovered = poolAfterFee >= normalPayout;
      const isOwn = p.creator.toLowerCase() === address.toLowerCase();
      return { pool: p, platformFee, normalPayout, maxPayout, fullyCovered, isOwn };
    });
  }, [pools, nft, address]);

  // Fully covered pools: cheapest spread first (= user keeps the most).
  const fullPools = ranked
    .filter((r) => r.fullyCovered)
    .sort((a, b) => a.pool.spreadBps - b.pool.spreadBps);

  // Partial coverage: rank by max_payout DESCENDING (= user gets the most WEGLD).
  // Spread doesn't really dominate here — pool size does.
  const partialPools = ranked
    .filter((r) => !r.fullyCovered)
    .sort((a, b) => {
      if (a.maxPayout === b.maxPayout) return 0;
      return a.maxPayout > b.maxPayout ? -1 : 1;
    });

  if (myNfts.length === 0) {
    return (
      <div className="card">
        <h3>No HLSR NFTs in your wallet</h3>
        <p className="muted">
          Connect a wallet that holds <span className="mono">{nftCfg.acceptedNftCollection}</span>{" "}
          NFTs (Hatom Liquid Staking unstake receipts) to swap them for instant WEGLD.
        </p>
      </div>
    );
  }

  return (
    <div className="col" style={{ gap: "1rem" }}>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Your HLSR NFTs</h3>
        <p className="muted" style={{ fontSize: "0.85rem", marginTop: 0 }}>
          Pick one to compare against available pools. The face value below is decoded from
          the NFT's on-chain attributes — the contract uses the same parser at swap time.
        </p>
        <div className="col" style={{ gap: "0.3rem", marginTop: "0.7rem" }}>
          {myNfts.map((n) => (
            <label
              key={n.nonce}
              className="row mono"
              style={{
                gap: "0.5rem",
                padding: "0.4rem 0.6rem",
                background:
                  selectedNonce === n.nonce ? "var(--bg-elev-2)" : "transparent",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="nft"
                checked={selectedNonce === n.nonce}
                onChange={() => setSelectedNonce(n.nonce)}
                style={{ width: "auto" }}
              />
              <span style={{ minWidth: "10rem" }}>
                {n.identifier}{" "}
                <span className="muted">(nonce {n.nonce})</span>
              </span>
              <span style={{ flex: 1 }} />
              <span>{fmtAmount(n.faceValue)} EGLD face</span>
              <span className="muted" style={{ fontSize: "0.8rem" }}>
                claim epoch {n.claimEpoch}
              </span>
            </label>
          ))}
        </div>
      </div>

      {nft && (
        <>
          {fullPools.length === 0 && partialPools.length === 0 ? (
            <div className="card">
              <p className="muted">No active pools on the contract right now.</p>
            </div>
          ) : (
            <>
              {fullPools.length > 0 && (
                <div className="col" style={{ gap: "0.5rem" }}>
                  <h4 style={{ margin: 0 }}>
                    Pools that cover your NFT in full
                  </h4>
                  <p className="muted" style={{ fontSize: "0.85rem", margin: 0 }}>
                    Sorted by least-loss-first (smallest spread).
                  </p>
                  {fullPools.map((r) => (
                    <PoolRow
                      key={r.pool.creator}
                      config={config}
                      nftCfg={nftCfg}
                      address={address}
                      nft={nft}
                      r={r}
                      onDone={onDone}
                    />
                  ))}
                </div>
              )}
              {partialPools.length > 0 && (
                <div className="col" style={{ gap: "0.5rem" }}>
                  <h4 style={{ margin: 0, color: "var(--danger)" }}>
                    ⚠ Pools that DO NOT fully cover your NFT
                  </h4>
                  <p className="muted" style={{ fontSize: "0.85rem", margin: 0 }}>
                    You will receive significantly less than the normal payout if you
                    swap with one of these. Only use if you absolutely need instant
                    liquidity and accept the loss.
                  </p>
                  {partialPools.map((r) => (
                    <PoolRow
                      key={r.pool.creator}
                      config={config}
                      nftCfg={nftCfg}
                      address={address}
                      nft={nft}
                      r={r}
                      onDone={onDone}
                      warned
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

type RankedPool = {
  pool: E.NftPool;
  platformFee: bigint;
  normalPayout: bigint;
  maxPayout: bigint;
  fullyCovered: boolean;
  isOwn: boolean;
};

function PoolRow({
  config,
  nftCfg,
  address,
  nft,
  r,
  onDone,
  warned = false,
}: {
  config: AppConfig;
  nftCfg: E.NftConfig;
  address: string;
  nft: E.UserHlsrNft;
  r: RankedPool;
  onDone: () => void;
  warned?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  async function execute() {
    setErr(null);
    setHash(null);
    setBusy(true);
    try {
      // min_payout = 95% of max to allow tiny rounding slippage.
      const minPayout = (r.maxPayout * 95n) / 100n;
      const tx = await E.buildNftSwap(
        config,
        address,
        r.pool.creator,
        nft.identifier.split("-").slice(0, 2).join("-"),
        nft.nonce,
        minPayout,
      );
      const h = await E.signAndSendNft(config, tx);
      setHash(h);
      await E.awaitCompletedNft(config, h);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
      setArmed(false);
    }
  }

  return (
    <div
      className="card"
      style={{
        background: r.isOwn
          ? "rgba(120, 120, 120, 0.04)"
          : warned
            ? "rgba(255, 70, 70, 0.06)"
            : undefined,
        border: r.isOwn
          ? "1px dashed var(--border)"
          : warned
            ? "1px solid var(--danger)"
            : undefined,
        opacity: r.isOwn ? 0.75 : 1,
      }}
    >
      <div className="row" style={{ flexWrap: "wrap", gap: "0.4rem" }}>
        <AddressChip address={r.pool.creator} network={config.network} />
        {r.isOwn && <span className="badge">your pool</span>}
        <span className="badge">spread {(r.pool.spreadBps / 100).toFixed(2)}%</span>
        <span className="badge">platform fee {(r.pool.platformFeeBps / 100).toFixed(2)}%</span>
        <span className="badge">{r.pool.swapsServed} swaps served</span>
        <div style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: "0.85rem" }}>
          pool has {fmtAmount(r.pool.wegld)} {nftCfg.wegldTokenId.split("-")[0]}
        </span>
      </div>
      <div className="row" style={{ marginTop: "0.6rem", flexWrap: "wrap", gap: "1.2rem" }}>
        <span>
          {r.isOwn ? "Would pay out: " : "You receive: "}
          <strong>{fmtAmount(r.maxPayout)} WEGLD</strong>
          {!r.fullyCovered && (
            <span style={{ color: "var(--danger)" }}>
              {" "}(normal: {fmtAmount(r.normalPayout)} — loss {fmtPercentLoss(r.normalPayout, r.maxPayout)})
            </span>
          )}
        </span>
        <span className="muted">
          Platform fee: {fmtAmount(r.platformFee)} WEGLD
        </span>
      </div>
      <div className="row" style={{ marginTop: "0.7rem", gap: "0.6rem" }}>
        {r.isOwn ? (
          <button disabled className="ghost" title="You can't swap into your own pool.">
            🚫 Your own pool — swap disabled
          </button>
        ) : !armed ? (
          <button
            onClick={() => setArmed(true)}
            disabled={busy}
            style={
              warned
                ? { background: "var(--danger)", color: "white" }
                : undefined
            }
          >
            {warned ? "Accept loss & swap…" : "Swap"}
          </button>
        ) : (
          <>
            <span style={{ color: warned ? "var(--danger)" : "var(--text)" }}>
              Confirm: send NFT, receive {fmtAmount(r.maxPayout)} WEGLD?
            </span>
            <button onClick={execute} disabled={busy}
              style={warned ? { background: "var(--danger)", color: "white" } : undefined}>
              {busy ? "Submitting…" : "Yes, swap"}
            </button>
            <button className="ghost" onClick={() => setArmed(false)} disabled={busy}>
              Cancel
            </button>
          </>
        )}
      </div>
      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={config} hash={hash} />}
    </div>
  );
}

function fmtPercentLoss(normal: bigint, actual: bigint): string {
  if (normal === 0n) return "—";
  const lostBps = ((normal - actual) * 10000n) / normal;
  return `${(Number(lostBps) / 100).toFixed(2)}%`;
}

// ─── My Pool view ──────────────────────────────────────────────────
function MyPoolView({
  config,
  address,
  nftCfg,
  myPool,
  onDone,
}: {
  config: AppConfig;
  address: string;
  nftCfg: E.NftConfig;
  myPool: E.NftPool | null;
  onDone: () => void;
}) {
  if (!myPool) {
    return <CreatePoolForm config={config} address={address} nftCfg={nftCfg} onDone={onDone} />;
  }
  return <ExistingPoolPanel config={config} address={address} nftCfg={nftCfg} pool={myPool} onDone={onDone} />;
}

function CreatePoolForm({
  config,
  address,
  nftCfg,
  onDone,
}: {
  config: AppConfig;
  address: string;
  nftCfg: E.NftConfig;
  onDone: () => void;
}) {
  const [spreadStr, setSpreadStr] = useState("1.0");
  const [amountStr, setAmountStr] = useState(fmtAmount(nftCfg.minDepositWegld));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  const spreadBps = Math.round(Number(spreadStr || "0") * 100);
  const spreadValid = spreadBps >= 50 && spreadBps <= 1000;
  const amountWei = amountStr ? parseAmount(amountStr) : 0n;
  const amountValid = amountWei >= nftCfg.minDepositWegld;

  async function submit() {
    setErr(null);
    setHash(null);
    setBusy(true);
    try {
      const tx = await E.buildCreateNftPool(
        config,
        address,
        nftCfg.wegldTokenId,
        amountWei,
        spreadBps,
      );
      const h = await E.signAndSendNft(config, tx);
      setHash(h);
      await E.awaitCompletedNft(config, h);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Create your liquidity pool</h3>
      <p className="muted">
        Park WEGLD here; users will sell you their HLSR NFTs at a discount you
        choose. You'll receive each NFT to your wallet and claim it yourself on
        <a href="https://app.hatom.com/liquid/multiversx" target="_blank" rel="noreferrer"> Hatom</a>{" "}
        after the 10-day cooldown.
      </p>

      <label className="muted">Initial deposit (WEGLD)</label>
      <input
        value={amountStr}
        onChange={(e) => setAmountStr(e.target.value)}
        placeholder={`min ${fmtAmount(nftCfg.minDepositWegld)} WEGLD`}
      />
      {!amountValid && (
        <p style={{ color: "var(--danger)", fontSize: "0.8rem", margin: "0.2rem 0" }}>
          must be at least {fmtAmount(nftCfg.minDepositWegld)} WEGLD
        </p>
      )}

      <label className="muted" style={{ marginTop: "0.4rem" }}>
        Spread % (your margin between NFT face value and what user receives)
      </label>
      <input
        value={spreadStr}
        onChange={(e) => setSpreadStr(e.target.value)}
        placeholder="1.0  (= 1%, range 0.5%–10%)"
      />
      {!spreadValid && (
        <p style={{ color: "var(--danger)", fontSize: "0.8rem", margin: "0.2rem 0" }}>
          spread must be in [0.5%, 10.0%]
        </p>
      )}
      <p className="muted" style={{ fontSize: "0.8rem" }}>
        Platform always takes <strong>50% of the spread</strong>. You keep the other 50%.
      </p>

      <div
        className="card"
        style={{
          marginTop: "0.6rem",
          background: "rgba(255, 200, 70, 0.07)",
          borderColor: "var(--platform)",
        }}
      >
        <strong>⚠ Anti-bot fee:</strong> if you withdraw without serving any
        swaps, you lose {fmtAmount(nftCfg.noSwapWithdrawFee)} WEGLD (sent to
        the platform). Once you serve at least one swap, all subsequent
        withdrawals are free.
      </div>

      <button
        onClick={submit}
        disabled={busy || !spreadValid || !amountValid}
        style={{ marginTop: "0.7rem" }}
      >
        {busy ? "Submitting…" : "Create pool"}
      </button>
      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={config} hash={hash} />}
    </div>
  );
}

function ExistingPoolPanel({
  config,
  address,
  nftCfg,
  pool,
  onDone,
}: {
  config: AppConfig;
  address: string;
  nftCfg: E.NftConfig;
  pool: E.NftPool;
  onDone: () => void;
}) {
  return (
    <div className="col" style={{ gap: "1rem" }}>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Your pool</h3>
        <div className="grid" style={{ gridTemplateColumns: "180px 1fr", rowGap: "0.45rem" }}>
          <span className="muted">Balance</span>
          <strong>{fmtAmount(pool.wegld)} WEGLD</strong>
          <span className="muted">Spread</span>
          <span>{(pool.spreadBps / 100).toFixed(2)}%</span>
          <span className="muted">Platform fee (50% of spread)</span>
          <span>{(pool.platformFeeBps / 100).toFixed(2)}%</span>
          <span className="muted">Swaps served</span>
          <span>{pool.swapsServed}</span>
          {pool.swapsServed === 0 && !pool.noSwapFeePaid && (
            <>
              <span className="muted">Next withdraw fee</span>
              <span style={{ color: "var(--danger)" }}>
                {fmtAmount(nftCfg.noSwapWithdrawFee)} WEGLD (one-time, until you
                serve a swap or pay it on first withdraw)
              </span>
            </>
          )}
          {pool.swapsServed === 0 && pool.noSwapFeePaid && (
            <>
              <span className="muted">Anti-bot fee</span>
              <span style={{ color: "var(--success)" }}>
                ✓ already paid — subsequent withdraws are free
              </span>
            </>
          )}
        </div>
      </div>

      <FundForm config={config} address={address} nftCfg={nftCfg} onDone={onDone} />
      <WithdrawForm config={config} address={address} nftCfg={nftCfg} pool={pool} onDone={onDone} />
      <ClosePoolForm config={config} address={address} nftCfg={nftCfg} pool={pool} onDone={onDone} />
    </div>
  );
}

function FundForm({
  config, address, nftCfg, onDone,
}: { config: AppConfig; address: string; nftCfg: E.NftConfig; onDone: () => void }) {
  const [amountStr, setAmountStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  const amount = amountStr ? parseAmount(amountStr) : 0n;
  const valid = amount > 0n;

  async function submit() {
    setErr(null);
    setHash(null);
    setBusy(true);
    try {
      const tx = await E.buildFundNftPool(config, address, nftCfg.wegldTokenId, amount);
      const h = await E.signAndSendNft(config, tx);
      setHash(h);
      await E.awaitCompletedNft(config, h);
      setAmountStr("");
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h4 style={{ marginTop: 0 }}>Add WEGLD to your pool</h4>
      <div className="row">
        <input
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value)}
          placeholder="amount of WEGLD"
        />
        <button onClick={submit} disabled={busy || !valid}>
          {busy ? "…" : "Add"}
        </button>
      </div>
      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={config} hash={hash} />}
    </div>
  );
}

function WithdrawForm({
  config, address, nftCfg, pool, onDone,
}: { config: AppConfig; address: string; nftCfg: E.NftConfig; pool: E.NftPool; onDone: () => void }) {
  const [amountStr, setAmountStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  const amount = amountStr ? parseAmount(amountStr) : 0n;
  const wouldChargeFee = pool.swapsServed === 0 && !pool.noSwapFeePaid;
  const valid =
    amount > 0n
    && amount <= pool.wegld
    && (!wouldChargeFee || amount > nftCfg.noSwapWithdrawFee);

  async function submit() {
    setErr(null);
    setHash(null);
    setBusy(true);
    try {
      const tx = await E.buildWithdrawNftPool(config, address, amount);
      const h = await E.signAndSendNft(config, tx);
      setHash(h);
      await E.awaitCompletedNft(config, h);
      setAmountStr("");
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h4 style={{ marginTop: 0 }}>Withdraw from your pool</h4>
      {wouldChargeFee && (
        <p style={{ color: "var(--danger)", fontSize: "0.85rem" }}>
          ⚠ You haven't served any swaps yet — this withdraw costs a one-time{" "}
          {fmtAmount(nftCfg.noSwapWithdrawFee)} WEGLD anti-bot fee. Amount
          must exceed the fee. After this charge, subsequent withdraws are free.
        </p>
      )}
      <div className="row">
        <input
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value)}
          placeholder={`up to ${fmtAmount(pool.wegld)} WEGLD`}
        />
        <button onClick={submit} disabled={busy || !valid}>
          {busy ? "…" : "Withdraw"}
        </button>
      </div>
      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={config} hash={hash} />}
    </div>
  );
}

function ClosePoolForm({
  config, address, nftCfg, pool, onDone,
}: { config: AppConfig; address: string; nftCfg: E.NftConfig; pool: E.NftPool; onDone: () => void }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);
  const wouldChargeFee = pool.swapsServed === 0 && !pool.noSwapFeePaid;

  async function submit() {
    setErr(null);
    setHash(null);
    setBusy(true);
    try {
      const tx = await E.buildCloseNftPool(config, address);
      const h = await E.signAndSendNft(config, tx);
      setHash(h);
      await E.awaitCompletedNft(config, h);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
      setArmed(false);
    }
  }

  return (
    <div className="card" style={{ borderColor: "var(--danger)" }}>
      <h4 style={{ marginTop: 0 }}>Close pool</h4>
      <p className="muted" style={{ fontSize: "0.85rem" }}>
        Drains everything back to you, applies the anti-bot fee if no swaps were
        served, and clears the pool state. You can create a new pool any time
        (with a different spread, for example).
      </p>
      {wouldChargeFee && (
        <p style={{ color: "var(--danger)", fontSize: "0.85rem" }}>
          ⚠ Will charge {fmtAmount(nftCfg.noSwapWithdrawFee)} WEGLD anti-bot fee.
        </p>
      )}
      {!armed ? (
        <button onClick={() => setArmed(true)}
          style={{ background: "var(--danger)", color: "white" }}>
          Close pool…
        </button>
      ) : (
        <div className="row">
          <span>Confirm closing the pool?</span>
          <button onClick={submit} disabled={busy}
            style={{ background: "var(--danger)", color: "white" }}>
            {busy ? "…" : "Yes, close"}
          </button>
          <button className="ghost" onClick={() => setArmed(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      )}
      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={config} hash={hash} />}
    </div>
  );
}

// ─── Admin view ────────────────────────────────────────────────────
function AdminView({
  config, address, nftCfg, onDone,
}: { config: AppConfig; address: string; nftCfg: E.NftConfig; onDone: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [hash, setHash] = useState<string | null>(null);

  async function run(label: string, build: () => Promise<any>) {
    setErr(null); setHash(null); setBusy(label);
    try {
      const tx = await build();
      const h = await E.signAndSendNft(config, tx);
      setHash(h);
      await E.awaitCompletedNft(config, h);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  }

  const [minDepStr, setMinDepStr] = useState(fmtAmount(nftCfg.minDepositWegld));
  const [feeStr, setFeeStr] = useState(fmtAmount(nftCfg.noSwapWithdrawFee));
  const [profitStr, setProfitStr] = useState(nftCfg.profitWallet);

  const newMinDep = minDepStr ? parseAmount(minDepStr) : 0n;
  const newFee = feeStr ? parseAmount(feeStr) : 0n;

  return (
    <div className="col" style={{ gap: "1rem" }}>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Admin</h3>

        <div className="row" style={{ gap: "0.8rem", flexWrap: "wrap", marginBottom: "0.6rem" }}>
          <button className="ghost"
            disabled={!!busy}
            onClick={() => run("pause", () => E.buildNftPause(config, address, !nftCfg.paused))}
            style={{ borderColor: nftCfg.paused ? "var(--success)" : "var(--danger)" }}>
            {busy === "pause" ? "…" : nftCfg.paused ? "▶ Unpause contract" : "⏸ Pause contract"}
          </button>
          <button className="ghost"
            disabled={!!busy}
            onClick={() => run("pauseExt", () => E.buildNftPauseExternal(config, address, !nftCfg.externalPaused))}>
            {busy === "pauseExt"
              ? "…"
              : nftCfg.externalPaused
                ? "Open pools to everyone"
                : "Restrict pools to owner only"}
          </button>
        </div>

        <p className="muted" style={{ fontSize: "0.85rem" }}>
          Contract paused: <strong>{nftCfg.paused ? "yes" : "no"}</strong>{" "}
          • Only-owner-pools: <strong>{nftCfg.externalPaused ? "yes" : "no"}</strong>
        </p>
      </div>

      <div className="card">
        <h4 style={{ marginTop: 0 }}>Min initial pool deposit</h4>
        <div className="row">
          <input value={minDepStr} onChange={(e) => setMinDepStr(e.target.value)} />
          <button
            disabled={!!busy || newMinDep === nftCfg.minDepositWegld}
            onClick={() => run("minDep", () => E.buildNftSetMinDeposit(config, address, newMinDep))}>
            {busy === "minDep" ? "…" : "Save"}
          </button>
        </div>
        <p className="muted" style={{ fontSize: "0.85rem" }}>
          Currently: {fmtAmount(nftCfg.minDepositWegld)} WEGLD
        </p>
      </div>

      <div className="card">
        <h4 style={{ marginTop: 0 }}>No-swap withdraw fee</h4>
        <div className="row">
          <input value={feeStr} onChange={(e) => setFeeStr(e.target.value)} />
          <button
            disabled={!!busy || newFee === nftCfg.noSwapWithdrawFee}
            onClick={() => run("fee", () => E.buildNftSetNoSwapFee(config, address, newFee))}>
            {busy === "fee" ? "…" : "Save"}
          </button>
        </div>
        <p className="muted" style={{ fontSize: "0.85rem" }}>
          Currently: {fmtAmount(nftCfg.noSwapWithdrawFee)} WEGLD
        </p>
      </div>

      <div className="card">
        <h4 style={{ marginTop: 0 }}>Profit wallet</h4>
        <div className="row">
          <input value={profitStr} onChange={(e) => setProfitStr(e.target.value)} placeholder="erd1…" />
          <button
            disabled={!!busy || !profitStr || profitStr === nftCfg.profitWallet}
            onClick={() => run("profit", () => E.buildNftSetProfitWallet(config, address, profitStr))}>
            {busy === "profit" ? "…" : "Save"}
          </button>
        </div>
        <p className="muted" style={{ fontSize: "0.85rem" }}>
          Currently:{" "}
          <span className="mono">{nftCfg.profitWallet}</span>{" "}
          <CopyButton value={nftCfg.profitWallet} />
        </p>
      </div>

      {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
      {hash && <TxLink cfg={config} hash={hash} />}

      <BotAdmin />
      <BlacklistAdmin />
      <GasConfigAdmin />
    </div>
  );
}

// ─── Bot admin section ───────────────────────────────────────────────
function BotAdmin() {
  const [s, setS] = useState<E.BotStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [interval, setIntervalStr] = useState("");
  const [message, setMessage] = useState("");
  const [minAge, setMinAge] = useState("");

  async function reload() {
    setErr(null);
    try {
      const st = await E.getBotStatus();
      setS(st);
      setIntervalStr(String(st.interval_minutes));
      setMessage(st.message);
      setMinAge(String(st.min_nft_age_seconds));
    } catch (e) {
      setErr(String(e));
    }
  }

  useEffect(() => { reload(); }, []);

  async function patch(label: string, body: Partial<E.BotStatus>) {
    setBusy(label); setErr(null);
    try {
      const st = await E.updateBotState(body);
      setS(st);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function tick() {
    setBusy("tick"); setErr(null);
    try {
      const r = await E.botTickNow();
      alert(`Tick result: ${JSON.stringify(r, null, 2)}`);
      reload();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  }

  if (!s) {
    return (
      <div className="card" style={{ marginTop: "1.5rem", borderColor: "var(--platform)" }}>
        <h3 style={{ margin: 0, color: "var(--platform)" }}>🤖 Outreach Bot</h3>
        {err ? (
          <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>
        ) : (
          <p className="muted">Loading bot state… (token required first time)</p>
        )}
        <button className="ghost" onClick={reload}>Retry / paste token</button>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: "1.5rem", borderColor: "var(--platform)" }}>
      <h3 style={{ margin: 0, color: "var(--platform)" }}>🤖 Outreach Bot</h3>
      <p className="muted" style={{ fontSize: "0.85rem", marginTop: "0.3rem" }}>
        Sends a small data-only tx ("{s.message}") to the holder of every new
        HLSR NFT minted on the configured network. Skips SC addresses and the
        blacklist below.
      </p>

      <div
        className="grid"
        style={{ gridTemplateColumns: "180px 1fr", rowGap: "0.4rem", marginTop: "0.6rem" }}
      >
        <span className="muted">Wallet</span>
        <span className="mono">{s.address || "(not configured)"}</span>
        <span className="muted">Network</span>
        <span>{s.network}</span>
        <span className="muted">Status</span>
        <span style={{ color: s.paused ? "var(--danger)" : "var(--success)" }}>
          {s.paused ? "⏸ paused" : "▶ running"}
        </span>
        <span className="muted">Tick interval</span>
        <span>{s.interval_minutes} min</span>
        <span className="muted">Min NFT age before sending</span>
        <span>{s.min_nft_age_seconds}s</span>
        <span className="muted">Last processed nonce</span>
        <span>{s.last_processed_nonce}</span>
        <span className="muted">Last processed at</span>
        <span>{s.last_processed_at ? new Date(s.last_processed_at).toLocaleString() : "—"}</span>
        <span className="muted">Total txs sent</span>
        <span>{s.txs_sent_total}</span>
        <span className="muted">Skipped (SC)</span>
        <span>{s.txs_skipped_sc}</span>
        <span className="muted">Skipped (blacklist)</span>
        <span>{s.txs_skipped_blacklist}</span>
        {s.last_error && (
          <>
            <span className="muted">Last error</span>
            <span className="mono" style={{ color: "var(--danger)", fontSize: "0.8rem" }}>{s.last_error}</span>
          </>
        )}
      </div>

      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.8rem" }}>
        <button
          className="ghost"
          disabled={!!busy}
          onClick={() => patch("pause", { paused: !s.paused })}
          style={{ borderColor: s.paused ? "var(--success)" : "var(--danger)" }}
        >
          {busy === "pause" ? "…" : s.paused ? "▶ Unpause" : "⏸ Pause"}
        </button>
        <button className="ghost" disabled={!!busy} onClick={tick}>
          {busy === "tick" ? "…" : "Run tick now"}
        </button>
      </div>

      <div className="row" style={{ marginTop: "0.8rem", gap: "0.6rem", flexWrap: "wrap" }}>
        <div className="col" style={{ gap: "0.2rem" }}>
          <label className="muted">Interval (minutes)</label>
          <div className="row">
            <input
              value={interval}
              onChange={(e) => setIntervalStr(e.target.value)}
              style={{ width: "5rem" }}
            />
            <button
              disabled={!!busy || Number(interval) === s.interval_minutes}
              onClick={() => patch("interval", { interval_minutes: Math.max(1, Number(interval)) })}
            >
              Save
            </button>
          </div>
        </div>
        <div className="col" style={{ gap: "0.2rem" }}>
          <label className="muted">Min NFT age (seconds)</label>
          <div className="row">
            <input
              value={minAge}
              onChange={(e) => setMinAge(e.target.value)}
              style={{ width: "6rem" }}
            />
            <button
              disabled={!!busy || Number(minAge) === s.min_nft_age_seconds}
              onClick={() => patch("min_age", { min_nft_age_seconds: Math.max(0, Number(minAge)) })}
            >
              Save
            </button>
          </div>
        </div>
      </div>

      <div className="col" style={{ marginTop: "0.6rem", gap: "0.2rem" }}>
        <label className="muted">Message (appears as tx data on the explorer)</label>
        <div className="row">
          <input value={message} onChange={(e) => setMessage(e.target.value)} />
          <button
            disabled={!!busy || message === s.message}
            onClick={() => patch("message", { message })}
          >
            Save
          </button>
        </div>
      </div>

      {err && <p className="mono" style={{ color: "var(--danger)", fontSize: "0.85rem" }}>{err}</p>}
    </div>
  );
}

// ─── Blacklist admin ─────────────────────────────────────────────────
function BlacklistAdmin() {
  const [list, setList] = useState<E.BlacklistEntry[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addr, setAddr] = useState("");
  const [reason, setReason] = useState("");
  const [filter, setFilter] = useState("");

  async function reload() {
    setErr(null);
    try {
      setList(await E.getBlacklist());
    } catch (e) {
      setErr(String(e));
    }
  }
  useEffect(() => { reload(); }, []);

  async function add() {
    setBusy(true); setErr(null);
    try {
      await E.blacklistAdd(addr, reason);
      setAddr(""); setReason("");
      await reload();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(a: string) {
    setBusy(true); setErr(null);
    try {
      await E.blacklistRemove(a);
      await reload();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  const filtered = filter
    ? list.filter(
        (e) =>
          e.address.toLowerCase().includes(filter.toLowerCase()) ||
          e.reason.toLowerCase().includes(filter.toLowerCase()),
      )
    : list;

  return (
    <div className="card" style={{ marginTop: "1.5rem" }}>
      <h3 style={{ marginTop: 0 }}>🚫 Outreach blacklist</h3>
      <p className="muted" style={{ fontSize: "0.85rem", margin: 0 }}>
        Wallets the bot will skip. SC addresses (erd1qqqq…) are auto-skipped
        by the bot — only add EOA wallets that complained.
      </p>

      <div className="row" style={{ marginTop: "0.7rem", gap: "0.4rem", flexWrap: "wrap" }}>
        <input
          value={addr}
          onChange={(e) => setAddr(e.target.value)}
          placeholder="erd1… address"
          style={{ flex: 2, minWidth: "12rem" }}
        />
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="reason (optional)"
          style={{ flex: 3, minWidth: "10rem" }}
        />
        <button onClick={add} disabled={busy || !addr.startsWith("erd1")}>
          {busy ? "…" : "Add"}
        </button>
      </div>

      <div className="row" style={{ marginTop: "0.7rem" }}>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter…"
        />
      </div>

      <div className="col" style={{ gap: "0.2rem", marginTop: "0.5rem" }}>
        {filtered.length === 0 ? (
          <p className="muted" style={{ fontSize: "0.85rem" }}>
            {list.length === 0 ? "No entries yet." : "No matches."}
          </p>
        ) : (
          filtered.map((e) => (
            <div
              key={e.address}
              className="row mono"
              style={{
                padding: "0.4rem 0.6rem",
                background: "var(--bg-elev-2)",
                borderRadius: 6,
                fontSize: "0.85rem",
                gap: "0.5rem",
              }}
            >
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                {e.address}
              </span>
              <span className="muted" style={{ flex: 1 }}>{e.reason || "—"}</span>
              <span className="muted" style={{ fontSize: "0.75rem" }}>
                {new Date(e.added_at).toLocaleDateString()}
              </span>
              <button
                className="ghost"
                onClick={() => remove(e.address)}
                disabled={busy}
                style={{ padding: "0.15rem 0.5rem", borderColor: "var(--danger)" }}
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>

      {err && <p className="mono" style={{ color: "var(--danger)", fontSize: "0.85rem" }}>{err}</p>}
    </div>
  );
}

// ─── Gas config admin ────────────────────────────────────────────────
function GasConfigAdmin() {
  const [cfg, setCfg] = useState<Record<string, number>>({});
  const [dirty, setDirty] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    setErr(null);
    try {
      const c = await E.getGasConfig();
      setCfg(c);
      setDirty({});
    } catch (e) {
      setErr(String(e));
    }
  }
  useEffect(() => { reload(); }, []);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const patch: Record<string, number> = {};
      for (const [k, v] of Object.entries(dirty)) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) patch[k] = Math.floor(n);
      }
      if (Object.keys(patch).length === 0) return;
      const next = await E.updateGasConfig(patch);
      setCfg(next);
      setDirty({});
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  const keys = Object.keys(cfg).filter((k) => !k.startsWith("_")).sort();
  const hasDirty = Object.keys(dirty).length > 0;

  return (
    <div className="card" style={{ marginTop: "1.5rem" }}>
      <h3 style={{ marginTop: 0 }}>⛽ Gas config</h3>
      <p className="muted" style={{ fontSize: "0.85rem", margin: 0 }}>
        Per-endpoint gas limits the dApp will use. Defaults sized for cross-shard
        tolerance. Unused gas is refunded, so it's safe to set generous values.
      </p>

      <div
        className="grid"
        style={{ gridTemplateColumns: "1fr 9rem", rowGap: "0.3rem", marginTop: "0.7rem", alignItems: "center" }}
      >
        {keys.map((k) => (
          <Fragment key={k}>
            <span className="mono" style={{ fontSize: "0.8rem" }}>{k}</span>
            <input
              value={k in dirty ? dirty[k] : String(cfg[k])}
              onChange={(e) => setDirty((d) => ({ ...d, [k]: e.target.value }))}
              style={{ fontSize: "0.85rem" }}
            />
          </Fragment>
        ))}
      </div>

      <div className="row" style={{ marginTop: "0.7rem", gap: "0.5rem" }}>
        <button onClick={save} disabled={busy || !hasDirty}>
          {busy ? "…" : "Save changes"}
        </button>
        <button className="ghost" onClick={reload} disabled={busy}>
          Reset
        </button>
      </div>

      {err && <p className="mono" style={{ color: "var(--danger)", fontSize: "0.85rem" }}>{err}</p>}
    </div>
  );
}

function TxLink({ cfg, hash }: { cfg: AppConfig; hash: string }) {
  const sub = cfg.network === "mainnet" ? "" : `${cfg.network}-`;
  return (
    <p className="mono muted" style={{ fontSize: "0.85rem" }}>
      tx:{" "}
      <a href={`https://${sub}explorer.multiversx.com/transactions/${hash}`} target="_blank" rel="noreferrer">
        {hash.slice(0, 14)}…
      </a>{" "}
      <ExplorerButton url={`https://${sub}explorer.multiversx.com/transactions/${hash}`} />
    </p>
  );
}
